import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { endImpersonation } from "../admin/api";
import { copy } from "../copy";
import { fullPageLoad } from "../navigate";
import { useSession } from "../session";
import BrandLogo from "../components/BrandLogo";
import Icon from "../components/Icon";

// Links to pages Go serves are plain anchors, so the browser makes a full
// request and the edge routes it to Go. Only paths in SPA_PATHS use <Link>.
// A wide link joins the desktop bar only at xl; it is always in the menu.
const pageLinks = [
  { href: "/contests", label: copy.nav.contests, inApp: true, wide: false },
  { href: "/ranking", label: copy.nav.rankings, inApp: true, wide: false },
  { href: "/earnings-calculator", label: copy.nav.calculator, inApp: false, wide: false },
  { href: "/how-it-works", label: copy.nav.howItWorks, inApp: true, wide: false },
  { href: "/websites-for-producers", label: copy.nav.websites, inApp: true, wide: true },
] as const;

function PageLink({ link, className, onClick }: { link: (typeof pageLinks)[number]; className: string; onClick?: () => void }) {
  return link.inApp ? (
    <Link to={link.href} onClick={onClick} className={className}>
      {link.label}
    </Link>
  ) : (
    <a href={link.href} onClick={onClick} className={className}>
      {link.label}
    </a>
  );
}

// In-page anchors exist only on the landing.
const scrollLinks = [
  { hash: "#producers", label: copy.nav.forProducers },
  { hash: "#fans", label: copy.nav.forFans },
  { hash: "#winners", label: copy.nav.winners },
] as const;

const linkClass =
  "bf-focusable whitespace-nowrap text-xs font-bold uppercase tracking-[0.16em] text-chrome-dim transition-colors hover:text-chrome";
const menuLinkClass =
  "bf-focusable border-b border-ground-edge/60 py-3 text-sm font-bold uppercase tracking-[0.14em] text-chrome";

function SessionSlot({ onNavigate }: { onNavigate?: () => void }) {
  const session = useSession();
  if (session.kind === "visitor") {
    return (
      <>
        <Link to="/login" onClick={onNavigate} className={linkClass}>
          {copy.nav.logIn}
        </Link>
        <Link
          to="/register"
          onClick={onNavigate}
          className="bf-focusable flex h-10 items-center whitespace-nowrap rounded-md border border-money/60 px-4 text-xs font-bold uppercase tracking-[0.14em] text-money transition-colors hover:bg-money/10"
        >
          {copy.nav.join}
        </Link>
      </>
    );
  }
  return (
    <>
      <span className="flex items-center gap-2 rounded-full border border-ground-edge bg-ground-raised py-1 pl-1 pr-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ground-edge font-headline text-sm uppercase text-gold">
          {session.username.slice(0, 1)}
        </span>
        <span className="max-w-[120px] truncate text-xs font-bold uppercase tracking-[0.12em] text-chrome">
          {session.username}
        </span>
      </span>
      <Link to="/dashboard" onClick={onNavigate} className={linkClass}>
        {copy.nav.dashboard}
      </Link>
      {session.kind === "admin" && (
        <Link to="/admin" onClick={onNavigate} className={linkClass}>
          {copy.nav.admin}
        </Link>
      )}
      {session.impersonating && <ReturnToAdmin />}
      {/* Go clears the session and redirects home, so this is a full page load. */}
      <a href="/logout" onClick={onNavigate} className={linkClass}>
        {copy.nav.logOut}
      </a>
    </>
  );
}

// An admin signed in as someone else goes back with one click. The session
// changes server-side, so the page reloads rather than routing.
function ReturnToAdmin() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const back = async () => {
    setBusy(true);
    setError("");
    const res = await endImpersonation();
    if (res.ok) {
      fullPageLoad("/admin");
      return;
    }
    setBusy(false);
    if (res.status === 403) {
      fullPageLoad("/login");
      return;
    }
    setError(res.error);
  };
  return (
    <>
      <button
        type="button"
        onClick={back}
        disabled={busy}
        className="bf-focusable flex h-10 items-center whitespace-nowrap rounded-md border border-gold/60 px-4 text-xs font-bold uppercase tracking-[0.14em] text-gold transition-colors hover:bg-gold/10 disabled:opacity-60"
      >
        {copy.nav.returnToAdmin}
      </button>
      {error && (
        <span role="alert" className="text-[11px] text-alert">
          {error}
        </span>
      )}
    </>
  );
}

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const onLanding = useLocation().pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setMenuOpen(false);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen ? "border-b border-ground-edge bg-ground/90 backdrop-blur-md" : "border-b border-transparent"
      }`}
    >
      <nav aria-label={copy.nav.label} className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" aria-label={copy.brand} className="bf-focusable flex shrink-0 items-center">
          <BrandLogo className="h-8 md:h-10" />
        </Link>

        {/* The bar holds the page links from lg; below that they are in the menu. */}
        <div className="hidden items-center gap-5 lg:flex xl:gap-7">
          {pageLinks.map((l) => (
            <PageLink key={l.href} link={l} className={l.wide ? `${linkClass} hidden xl:block` : linkClass} />
          ))}
          {onLanding &&
            scrollLinks.map((l) => (
              <a key={l.hash} href={l.hash} className={`${linkClass} hidden 2xl:block`}>
                {l.label}
              </a>
            ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-3 md:flex">
            <SessionSlot />
          </div>
          {onLanding && (
            <a
              href="#live-contests"
              className="bf-focusable hidden h-10 items-center whitespace-nowrap rounded-md bg-money px-5 text-xs font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep lg:flex"
            >
              {copy.nav.makePick}
            </a>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={copy.nav.menu}
            className="bf-focusable flex h-10 w-10 items-center justify-center rounded-md border border-ground-edge text-lg text-chrome lg:hidden"
          >
            <Icon name={menuOpen ? "close" : "menu"} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="bf-fade-in border-t border-ground-edge bg-ground px-4 pb-5 pt-3 lg:hidden">
          <div className="flex flex-col">
            {pageLinks.map((l) => (
              <PageLink key={l.href} link={l} onClick={close} className={menuLinkClass} />
            ))}
            {onLanding &&
              scrollLinks.map((l) => (
                <a key={l.hash} href={l.hash} onClick={close} className={menuLinkClass}>
                  {l.label}
                </a>
              ))}
          </div>
          {/* From md the bar already shows the session links. */}
          <div className="mt-4 flex flex-wrap items-center gap-3 md:hidden">
            <SessionSlot onNavigate={close} />
          </div>
        </div>
      )}
    </header>
  );
}
